import type { HealthCertificateRow, HealthCertificateSummary, HealthStatus } from "./types";

export const HEALTH_STATUS_LABELS: Array<{ label: string; value: HealthStatus }> = [
  { label: "正常", value: "normal" },
  { label: "临期", value: "expiring" },
  { label: "过期", value: "expired" },
  { label: "缺失", value: "missing" }
];

export function buildHealthCertificateSummary(rows: HealthCertificateRow[], now = new Date()): HealthCertificateSummary {
  const counts: Record<HealthStatus, number> = {
    normal: 0,
    expiring: 0,
    expired: 0,
    missing: 0
  };
  const normalizedRows = rows.map((row) => normalizeHealthCertificateRow(row, now));

  for (const row of normalizedRows) {
    counts[row.status] += 1;
  }

  return {
    counts,
    rows: normalizedRows,
    total: normalizedRows.length
  };
}

export function buildPublicHealthCertificateSummary(rows: HealthCertificateRow[], now = new Date()): HealthCertificateSummary {
  const summary = buildHealthCertificateSummary(rows, now);

  return {
    ...summary,
    rows: summary.rows.map(({ idNumber: _idNumber, ...row }) => row)
  };
}

export function normalizeHealthCertificateRow(row: HealthCertificateRow, now = new Date()): HealthCertificateRow {
  const idNumber = typeof row.idNumber === "string" ? row.idNumber.trim() : "";
  const hasHealthCertificate = resolveHasHealthCertificate(row);
  const status = calculateHealthStatus({ ...row, hasHealthCertificate }, now);

  return {
    ...row,
    idNumber,
    idNumberMasked: idNumber ? maskIdNumber(idNumber) : row.idNumberMasked || "",
    hasHealthCertificate,
    status
  };
}

export function calculateHealthStatus(row: Pick<HealthCertificateRow, "hasHealthCertificate" | "status" | "validUntil">, now = new Date()): HealthStatus {
  if (resolveHasHealthCertificate(row) === false) {
    return "missing";
  }

  const validUntil = parseDateOnly(row.validUntil);
  if (!validUntil) {
    return "missing";
  }

  const today = startOfLocalDay(now);
  if (validUntil.getTime() < today.getTime()) {
    return "expired";
  }

  if (validUntil.getTime() <= addOneMonth(today).getTime()) {
    return "expiring";
  }

  return "normal";
}

export function maskIdNumber(value: string): string {
  const normalized = value.trim().replaceAll(/\s/g, "");

  if (!normalized) {
    return "";
  }
  if (normalized.includes("*")) {
    return normalized;
  }
  if (normalized.length <= 6) {
    return "*".repeat(normalized.length);
  }

  const headLength = normalized.length >= 11 ? 6 : 3;
  const tailLength = normalized.length >= 11 ? 4 : 2;
  const hiddenLength = Math.max(4, normalized.length - headLength - tailLength);

  return `${normalized.slice(0, headLength)}${"*".repeat(hiddenLength)}${normalized.slice(-tailLength)}`;
}

function resolveHasHealthCertificate(row: Pick<HealthCertificateRow, "hasHealthCertificate" | "status">): boolean {
  if (typeof row.hasHealthCertificate === "boolean") {
    return row.hasHealthCertificate;
  }

  return row.status !== "missing";
}

function parseDateOnly(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addOneMonth(date: Date): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + 1);
  return next;
}
